import React, { useState, useEffect, useRef } from "react";
import { Box, Container, Typography } from "@mui/material";
import axios from "axios";
import { useHistory } from "react-router-dom";
import web3 from "../../../Service/web3";
import "../../counter.css";
import "./hero.css";
import { colors } from "../../../../theme/colors";
import HeroStats from "./heroStates.js";
import HeroActions from "./heroActions";
import { AutoBetGlobe } from "./three";
import Ticker from "./ticker";
import heroBackground from "../../../../static/Images/heroBackground.png";
import {
  startMarqueeAnimation,
  startHeroBackgroundAnimation,
  startGlobeParallaxAnimation,
  startHeroEntryAnimation,
  cleanupAnimation,
  trustShineSx,
} from "../index.js";

const Hero = ({ item, bookies, lotteries, buytotal, draws, wins, variant }) => {
  const [featuredNews, setFeaturedNews] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [winnerNotifications, setWinnerNotifications] = useState([]);
  const [statsReady, setStatsReady] = useState(false);

  const autoBetRef = useRef(null);
  const heroBgRef = useRef(null);
  const heroContentRef = useRef(null);
  const globeRef = useRef(null);

  const apiUrl = process.env.REACT_APP_API_KEY;
  const history = useHistory();

  useEffect(() => {
    const animation = startMarqueeAnimation(autoBetRef);
    return () => cleanupAnimation(animation);
  }, []);

  useEffect(() => {
    const bgAnimation = startHeroBackgroundAnimation(heroBgRef);
    const globeAnimation = startGlobeParallaxAnimation(globeRef);

    return () => {
      if (bgAnimation) bgAnimation.revert();
      if (globeAnimation) globeAnimation.revert();
    };
  }, []);

  useEffect(() => {
    let animation;

    const startAnimation = () => {
      animation = startHeroEntryAnimation(heroContentRef, () => {
        setStatsReady(true);
      });
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(startAnimation);
    });

    return () => cleanupAnimation(animation);
  }, []);

  useEffect(() => {
    axios.get(`${apiUrl}/news/getAllNews`).then((response) => {
      const newsData = response.data.result;
      const featuredNewsData = newsData.filter((news) => news.featured);
      setFeaturedNews(featuredNewsData);
    });

    const fetchData = async () => {
      try {
        const account = await web3.eth.getAccounts();
        let skip = 0;
        let allNotifications = [];
        let allWinnerNotifications = [];

        const fetchNotifications = async () => {
          const response = await fetch(
            `${apiUrl}/notifications?limit=10&skip=${skip}`,
            {
              method: "GET",
              headers: { Authorization: account[0] },
            }
          );

          if (response.ok) {
            const data = await response.json();

            const filteredNotifications = data.result.filter(
              (notification) =>
                notification.type === "LotteryCreatedAdmin" ||
                notification.type === "LotteryCreated"
            );

            if (filteredNotifications.length > 0) {
              allNotifications = [
                ...allNotifications,
                ...filteredNotifications,
              ];
              skip += 10;
              await fetchNotifications();
            } else {
              setNotifications(allNotifications);
            }

            const filteredWinnerNotifications = data.result.filter(
              (notification) =>
                notification.type === "LotteryResult" ||
                notification.type === "SpinLotteryResult" ||
                notification.type === "LotteryWinner"
            );

            if (filteredWinnerNotifications.length > 0) {
              allWinnerNotifications = [
                ...allWinnerNotifications,
                ...filteredWinnerNotifications,
              ];
              skip += 10;
              await fetchNotifications();
            } else {
              setWinnerNotifications(allWinnerNotifications);
            }
          } else {
            console.error("Error fetching notifications:", response.error);
          }
        };

        await fetchNotifications();
      } catch (error) {
        console.error("Error fetching notifications:", error);
      }
    };

    fetchData();
  }, []);

  function handleRedirect(notification) {
    switch (notification.type) {
      case "LotteryCreated":
        if (notification.lotteryName === "Missile")
          history.push(`/missilelottery/${notification.genId}`);
        else if (notification.lotteryName === "Mine")
          history.push(`/minelottery/${notification.genId}`);
        else if (notification.lotteryName === "MRL")
          history.push(`/lottery/${notification.genId}`);
        else history.push("/spinnerlottery");
        break;
      case "LotteryCreatedAdmin":
        if (notification.lotteryName === "Missile")
          history.push(`/missilelottery/${notification.genId}`);
        else if (notification.lotteryName === "Mine")
          history.push(`/minelottery/${notification.genId}`);
        else if (notification.lotteryName === "MRL")
          history.push(`/lottery/${notification.genId}`);
        else history.push("/spinnerlottery");
        break;
      default:
        break;
    }
  }

  return (
    <Container maxWidth={false} disableGutters sx={{ p: 0, pt:'80px' }}>
      <Ticker
        featuredNews={featuredNews}
        notifications={notifications}
        winnerNotifications={winnerNotifications}
        handleRedirect={handleRedirect}
      />

      <Box
        className="banner-section"
        sx={{
          position: "relative",
          minHeight: "auto",
          height: "auto",
          overflow: "hidden",
          background: (theme) => theme.palette.background.default,
        }}
      >
        <Box
          ref={heroBgRef}
          className="hero-bg-layer"
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 0,
            backgroundImage: `url(${heroBackground})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            willChange: "transform",
          }}
        />

        <Box
          ref={globeRef}
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: "100%",
            height: "100%",
            zIndex: 1,
            pointerEvents: "none",
            willChange: "transform",
          }}
        >
          <AutoBetGlobe />
        </Box>

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 2,
            background: {
              xs: "rgba(255, 255, 255, 0.2)",
              sm: (theme) =>
                `linear-gradient(90deg, ${theme.custom.colors.alpha.cream[96]} 0%, ${theme.custom.colors.alpha.cream[88]} 32%, ${theme.custom.colors.alpha.cream[30]} 62%, ${theme.custom.colors.alpha.cream[5]} 100%)`,
            },
            backdropFilter: { xs: "blur(2px)", sm: "none" },
            WebkitBackdropFilter: { xs: "blur(2px)", sm: "none" },
            pointerEvents: "none",
          }}
        />

          <Box
            className="banner-content-area"
            sx={(theme) => ({
              position: "relative",
              zIndex: 2,
              height: {
                xs: "100%",
                sm: "auto",
                md: "auto",
              },
              display: "flex",
              alignItems: "stretch",
              flexDirection: "column",
              pt: theme.custom.layoutSizes.section.paddingTop,
              pb: theme.custom.layoutSizes.section.paddingBottom,
            })}
          >
          <Container
            maxWidth={false}
            disableGutters
            sx={(theme) => ({
              width: "100%",
              maxWidth: "1400px",
              mx: "auto",
              px: theme.custom["home-section-left-padding"],
              pt: 0,
              pb: 0,
              flex: {
                xs: 1,
                sm: "none",
                md: "none",
              },
              display: "flex",
              flexDirection: "column",
            })}
          >
            <Box
              ref={heroContentRef}
              sx={{
                width: "100%",
                maxWidth: "900px",
                flex: {
                  xs: 1,
                  sm: "none",
                  md: "none",
                },
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                className={`banner-content${
                  variant === "home" ? " home-hero" : ""
                }`}
                sx={(theme) => ({
                  width: "100%",
                  justifyContent: "flex-start",
                  flex: {
                    xs: 1,
                    sm: "none",
                    md: "none",
                  },
                  display: "flex",
                  flexDirection: "column",
                  gap: theme.custom.layoutSizes.section.gap,
                })}
              >
                <Box sx={(theme) => theme.typography.homeSection?.headerContainer || {}}>
                  {variant === "home" && (
                    <Box
                      component="p"
                      className="home-kicker hero-entry-item"
                      sx={(theme) => ({
                        ...(theme.typography.homeSection?.eyebrow || {}),
                        color: theme.palette.text.primary,
                        "&::before": {
                          ...(theme.typography.homeSection?.eyebrow?.["&::before"] || {}),
                          background: theme.palette.text.primary,
                        },
                      })}
                    >
                      DECENTRALIZED LOTTERY & GAMING ECOSYSTEM
                    </Box>
                  )}

                  <Typography
                    component="h1"
                    className="title hero-entry-item"
                    sx={(theme) => ({
                      ...(theme.typography.homeSection?.heading || {}),
                      textAlign: "left",
                      color: theme.palette.text.primary,
                      whiteSpace: { xs: "normal", sm: "nowrap" },
                    })}
                  >
                    Play. Win.{" "}
                    <Box
                      component="span"
                      sx={(theme) => ({
                        ...trustShineSx(theme.palette.primary.main),
                        fontSize: { xs: "1.2em", sm: "1.2em" },
                        fontWeight: 900,
                        WebkitTextStroke: `1.5px ${theme.palette.primary.main}`,
                      })}
                    >
                      Trust.
                    </Box>
                  </Typography>

                  <Typography
                    component="p"
                    className="hero-entry-item"
                    sx={(theme) => ({
                      ...(theme.typography.homeSection?.description || {}),
                      color: {
                        xs: theme.palette.text.primary,
                        sm: theme.palette.text.secondary,
                      },
                      textShadow: {
                        xs: `0 0 4px ${theme.palette.background.default || '#ffffff'}, 0 0 1px ${theme.palette.background.default || '#ffffff'}`,
                        sm: "none",
                      },
                      maxWidth: "800px",
                    })}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: { xs: "none", sm: "inline" },
                        fontSize: "inherit",
                      }}
                    >
                      AutoBET is a next-generation blockchain lottery and gaming
                      platform featuring fair, transparent and globally
                      accessible games like Powerball, Revolver, Pokies and more
                      — all powered by smart contracts.
                    </Box>

                    <Box
                      component="span"
                      sx={{
                        display: { xs: "inline", sm: "none" },
                        fontSize: "inherit",
                      }}
                    >
                      AutoBET is a fair, transparent and globally accessible
                      lottery and gaming platform with games like Powerball,
                      Revolver, Pokies and more — powered by smart contracts.
                    </Box>
                  </Typography>
                </Box>

                <Box className="hero-entry-item">
                  <HeroStats
                    bookies={bookies}
                    lotteries={lotteries}
                    buytotal={buytotal}
                    draws={draws}
                    wins={wins}
                    startAnimation={statsReady}
                  />
                </Box>

                {variant === "home" && (
                  <Box
                    className="hero-entry-item"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <HeroActions history={history} />
                  </Box>
                )}
              </Box>
            </Box>
          </Container>
        </Box>
      </Box>
    </Container>
  );
};

export default Hero;
